import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: ""
      });
    } catch (error) {
      setSubmitError("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="contact-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070"
            alt="Contact us"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sky-400 font-bold uppercase tracking-widest text-sm mb-4 block"
            >
              Contact Us
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="heading-primary text-white mb-6"
            >
              Get In Touch With Our Team
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-300"
            >
              Ready to transform your property? Contact us today for a free, 
              no-obligation quote.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section-padding bg-white" data-testid="contact-main">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="heading-tertiary text-slate-900 mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Phone</h4>
                    <a 
                      href="tel:+1-888-435-4674" 
                      className="text-sky-600 hover:text-sky-700 font-semibold text-lg"
                      data-testid="contact-phone-link"
                    >
                      +1-888-435-4674
                    </a>
                    <p className="text-slate-500 text-sm mt-1">Click to call directly</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Email</h4>
                    <a 
                      href="mailto:support@buyvistas.store" 
                      className="text-sky-600 hover:text-sky-700 font-semibold"
                      data-testid="contact-email-link"
                    >
                      support@buyvistas.store
                    </a>
                    <p className="text-slate-500 text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Address</h4>
                    <p className="text-slate-600">
                      20 E 22nd St #4699<br />
                      New York, NY 10165
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Business Hours</h4>
                    <p className="text-slate-600">
                      Monday - Friday: 7:00 AM - 7:00 PM<br />
                      Saturday: 8:00 AM - 5:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Click to Call Button */}
              <a 
                href="tel:+1-888-435-4674"
                className="mt-8 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                data-testid="click-to-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 p-8 lg:p-12 border border-slate-200">
                <h2 className="heading-tertiary text-slate-900 mb-2">
                  Send Us a Message
                </h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>
                
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                    data-testid="contact-success"
                  >
                    <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Message Sent Successfully!</h3>
                    <p className="text-slate-600 mb-6">
                      Thank you for contacting BuyVistas. We've received your message and 
                      will respond within 24 hours.
                    </p>
                    <Button
                      onClick={() => setSubmitSuccess(false)}
                      className="bg-sky-600 hover:bg-sky-700"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Full Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                          className="rounded-none h-12"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          required
                          className="rounded-none h-12"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          className="rounded-none h-12"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Service Interested In
                        </label>
                        <Select onValueChange={(value) => handleSelectChange("service", value)}>
                          <SelectTrigger className="rounded-none h-12" data-testid="contact-service-select">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="house-washing">House Washing</SelectItem>
                            <SelectItem value="driveway-cleaning">Driveway Cleaning</SelectItem>
                            <SelectItem value="deck-patio">Deck & Patio Cleaning</SelectItem>
                            <SelectItem value="roof-cleaning">Roof Cleaning</SelectItem>
                            <SelectItem value="commercial">Commercial Washing</SelectItem>
                            <SelectItem value="sidewalk">Sidewalk Cleaning</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your project, property size, and any specific concerns..."
                        rows={6}
                        required
                        className="rounded-none"
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    {submitError && (
                      <p className="text-red-600 text-sm" data-testid="contact-error">{submitError}</p>
                    )}
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 uppercase tracking-wider rounded-none flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-slate-100" data-testid="contact-map">
        <div className="h-[400px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9476844069487!2d-73.98902708459473!3d40.740566179328865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a3f73a7327%3A0x8f5e9a5d8a2e0a5!2s20%20E%2022nd%20St%2C%20New%20York%2C%20NY%2010010!5e0!3m2!1sen!2sus!4v1703000000000!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="BuyVistas Location"
          />
        </div>
      </section>
    </div>
  );
}
